import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Upload, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const ASSETS = 'https://customer-assets-jai6qajn.emergentagent.net/job_cheratina-b2b/artifacts';
const SHOWROOM = `${ASSETS}/jaua37gk_reception%20showroom%20%2B%20commercial%20office.png`;

const FORMATS = [
  { id: 'pellets', label: 'Pellets · Granuli' },
  { id: 'powder',  label: 'Powder · Polvere' },
  { id: 'strips',  label: 'Strips' },
  { id: 'roll',    label: 'Strips in Roll' },
  { id: 'stick',   label: 'Sticks' },
];

const COLORS = [
  { id: 'transparent',  label: 'Transparent', hex: '#F2ECE0' },
  { id: 'blonde',       label: 'Blonde',      hex: '#C5A059' },
  { id: 'dark_blonde',  label: 'Dark Blonde', hex: '#8A6D3A' },
  { id: 'brown',        label: 'Brown',       hex: '#6B4423' },
  { id: 'dark_brown',   label: 'Dark Brown',  hex: '#3D2817' },
  { id: 'black',        label: 'Black',       hex: '#1A1108' },
];

const SIZES = [
  { id: '5kg',  label: '5 kg',  desc: 'Trial · Sample' },
  { id: '10kg', label: '10 kg', desc: 'Small run' },
  { id: '20kg', label: '20 kg', desc: 'Standard' },
  { id: '25kg', label: '25 kg', desc: 'Industrial' },
];

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function PrivateLabelPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [f, setF] = useState({
    company_name: '', contact_name: '', country: '', email: '', phone: '',
    formats: [], colors: [], package_sizes: [],
    brand_name: '', logo_data_url: '',
    estimated_total_kg: '', delivery_deadline: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  const toggle = (key, id) => {
    setF(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
    });
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo max 2 MB'); return; }
    const b64 = await toBase64(file);
    setF(p => ({ ...p, logo_data_url: b64 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name || !f.country || !f.email || !f.brand_name) {
      toast.error(t('oem.err_required'));
      return;
    }
    if (f.formats.length === 0 || f.colors.length === 0 || f.package_sizes.length === 0) {
      toast.error(t('oem.err_selection'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/private-label-requests', f);
      setOk(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(t('oem.err_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  if (ok) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center px-6 py-24">
        <div className="max-w-xl text-center" data-testid="oem-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#C5A059] flex items-center justify-center mb-8">
            <Check className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
          <div className="text-xs uppercase tracking-[0.4em] text-[#C5A059] mb-4">— {t('oem.success_eyebrow')}</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl mb-6 leading-tight">
            {t('oem.success_title_1')} <em className="text-[#C5A059] not-italic">{t('oem.success_title_2')}</em>
          </h1>
          <p className="font-serif italic text-lg text-[#3D2817]/70 mb-10">
            {t('oem.success_body')} <span className="text-[#C5A059]">sales@cosmetissagekeratin.com</span>.
          </p>
          <button
            onClick={() => nav('/')}
            className="inline-flex items-center gap-3 border border-[#3D2817] hover:bg-[#3D2817] hover:text-white px-8 py-4 text-sm uppercase tracking-[0.25em] transition-all"
            data-testid="oem-back-home"
          >
            {t('oem.back_home')} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      {/* HERO */}
      <section className="relative bg-[#3D2817] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={SHOWROOM} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3D2817] via-[#3D2817]/70 to-transparent" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="text-xs uppercase tracking-[0.5em] text-[#C5A059] mb-6">— {t('oem.eyebrow')}</div>
          <h1 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] max-w-3xl">
            {t('oem.title_1')} <br />
            <em className="text-[#C5A059] not-italic">{t('oem.title_2')}</em>.
          </h1>
          <p className="mt-6 font-serif italic text-xl md:text-2xl text-white/70 max-w-2xl">
            {t('oem.subtitle')}
          </p>
        </div>
      </section>

      {/* FORM */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <form onSubmit={submit} className="space-y-16" data-testid="oem-form">
          {/* STEP 1 — Company */}
          <div>
            <StepHeader n="01" title={t('oem.s1_title')} tStep={t('oem.step')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label={`${t('quote.company')}*`} value={f.company_name} onChange={v => setF({ ...f, company_name: v })} testid="oem-company" />
              <Field label={t('quote.contact')}       value={f.contact_name} onChange={v => setF({ ...f, contact_name: v })} testid="oem-contact" />
              <Field label={`${t('quote.country')}*`} value={f.country}      onChange={v => setF({ ...f, country: v })}      testid="oem-country" />
              <Field label={`${t('quote.email')}*`}   type="email" value={f.email} onChange={v => setF({ ...f, email: v })}  testid="oem-email" />
              <Field label={`${t('quote.phone')} / WhatsApp`} value={f.phone} onChange={v => setF({ ...f, phone: v })} testid="oem-phone" />
            </div>
          </div>

          {/* STEP 2 — Formats */}
          <div>
            <StepHeader n="02" title={t('oem.s2_title')} hint={t('oem.s2_hint')} tStep={t('oem.step')} />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
              {FORMATS.map(x => (
                <ChipButton
                  key={x.id}
                  selected={f.formats.includes(x.id)}
                  onClick={() => toggle('formats', x.id)}
                  testid={`oem-format-${x.id}`}
                >
                  {t(`formats.${x.id === 'pellets' ? 'granuli' : x.id === 'powder' ? 'polvere' : x.id}`)}
                </ChipButton>
              ))}
            </div>
          </div>

          {/* STEP 3 — Colours */}
          <div>
            <StepHeader n="03" title={t('oem.s3_title')} hint={t('oem.s3_hint')} tStep={t('oem.step')} />
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {COLORS.map(c => {
                const active = f.colors.includes(c.id);
                const key = c.id.replace('_', '_');
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => toggle('colors', c.id)}
                    data-testid={`oem-color-${c.id}`}
                    className={`group relative border transition-all ${active ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-[#3D2817]/15 hover:border-[#C5A059]/60'}`}
                  >
                    <div className="aspect-square" style={{ background: c.hex }} />
                    <div className="p-2 text-center">
                      <div className="text-[10px] uppercase tracking-widest text-[#3D2817]">{t(`colors.${key === 'transparent' ? 'trasparente' : key === 'black' ? 'nero' : key === 'blonde' ? 'biondo' : key === 'dark_blonde' ? 'biondo_scuro' : key === 'brown' ? 'marrone' : 'marrone_scuro'}`)}</div>
                    </div>
                    {active && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#C5A059] flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* STEP 4 — Packaging sizes */}
          <div>
            <StepHeader n="04" title={t('oem.s4_title')} hint={t('oem.s4_hint')} tStep={t('oem.step')} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {[
                { id: '5kg',  label: '5 kg',  desc: t('bulk.target_sample') },
                { id: '10kg', label: '10 kg', desc: t('bulk.target_small') },
                { id: '20kg', label: '20 kg', desc: t('bulk.target_standard') },
                { id: '25kg', label: '25 kg', desc: t('bulk.target_industrial') },
              ].map(s => (
                <ChipButton
                  key={s.id}
                  selected={f.package_sizes.includes(s.id)}
                  onClick={() => toggle('package_sizes', s.id)}
                  testid={`oem-size-${s.id}`}
                  block
                >
                  <div className="font-serif text-3xl leading-none mb-1">{s.label}</div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">{s.desc}</div>
                </ChipButton>
              ))}
            </div>
          </div>

          {/* STEP 5 — Branding */}
          <div>
            <StepHeader n="05" title={t('oem.s5_title')} hint={t('oem.s5_hint')} tStep={t('oem.step')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label={t('oem.brand_name')} value={f.brand_name} onChange={v => setF({ ...f, brand_name: v })} testid="oem-brand-name" />
              <div>
                <label className="block text-xs uppercase tracking-[0.3em] text-[#3D2817]/60 mb-2">{t('oem.logo_upload_label')}</label>
                {f.logo_data_url ? (
                  <div className="flex items-center gap-4 p-3 border border-[#C5A059]/40 bg-white">
                    <img src={f.logo_data_url} alt="Uploaded logo" className="w-16 h-16 object-contain" />
                    <div className="flex-1 text-sm text-[#3D2817]/70">{t('oem.logo_uploaded')}</div>
                    <button type="button" onClick={() => setF(p => ({ ...p, logo_data_url: '' }))} data-testid="oem-logo-remove" className="text-[#C5A059] hover:text-[#3D2817]">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <label
                    className="flex items-center justify-center gap-2 p-4 border border-dashed border-[#3D2817]/25 hover:border-[#C5A059] cursor-pointer bg-white/50 hover:bg-white transition-colors text-sm text-[#3D2817]/70"
                    data-testid="oem-logo-upload"
                  >
                    <Upload className="w-4 h-4 text-[#C5A059]" />
                    <span>{t('oem.logo_upload_action')}</span>
                    <input type="file" accept="image/*" onChange={onLogo} className="hidden" />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* STEP 6 — Volume & timeline */}
          <div>
            <StepHeader n="06" title={t('oem.s6_title')} tStep={t('oem.step')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label={t('oem.qty_label')} value={f.estimated_total_kg} onChange={v => setF({ ...f, estimated_total_kg: v })} testid="oem-qty" placeholder={t('oem.qty_placeholder')} />
              <Field label={t('oem.deadline_label')} value={f.delivery_deadline} onChange={v => setF({ ...f, delivery_deadline: v })} testid="oem-deadline" placeholder={t('oem.deadline_placeholder')} />
            </div>
            <div className="mt-6">
              <label className="block text-xs uppercase tracking-[0.3em] text-[#3D2817]/60 mb-2">{t('oem.notes_label')}</label>
              <textarea
                value={f.notes}
                onChange={e => setF({ ...f, notes: e.target.value })}
                data-testid="oem-notes"
                rows={4}
                placeholder={t('oem.notes_placeholder')}
                className="w-full bg-white border border-[#3D2817]/15 focus:border-[#C5A059] outline-none px-4 py-3 text-sm font-sans transition-colors"
              />
            </div>
          </div>

          {/* SUBMIT */}
          <div className="pt-6 border-t border-[#3D2817]/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="font-serif italic text-sm text-[#3D2817]/60 max-w-md">
              {t('oem.reply_promise')}
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="oem-submit"
              className="group inline-flex items-center justify-center gap-3 bg-[#3D2817] hover:bg-[#C5A059] text-white px-10 py-5 text-sm uppercase tracking-[0.3em] transition-all disabled:opacity-50"
            >
              {submitting ? t('oem.submitting') : (<>{t('oem.submit')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>)}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function StepHeader({ n, title, hint, tStep }) {
  return (
    <div className="mb-8 border-l-2 border-[#C5A059] pl-5">
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059] mb-1">— {tStep || 'Step'} {n}</div>
      <h2 className="font-serif text-3xl md:text-4xl font-light leading-tight text-[#3D2817]">{title}</h2>
      {hint && <p className="mt-2 font-serif italic text-sm text-[#3D2817]/50">{hint}</p>}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid, placeholder }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.3em] text-[#3D2817]/60 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className="w-full bg-white border border-[#3D2817]/15 focus:border-[#C5A059] outline-none px-4 py-3 text-sm font-sans transition-colors"
      />
    </div>
  );
}

function ChipButton({ selected, onClick, children, testid, block }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`${block ? 'p-5 text-center' : 'px-5 py-4 text-sm'} border transition-all ${
        selected
          ? 'border-[#C5A059] bg-[#C5A059] text-white'
          : 'border-[#3D2817]/15 bg-white text-[#3D2817] hover:border-[#C5A059]'
      }`}
    >
      {children}
    </button>
  );
}
